import React, { useState, useRef, useEffect } from 'react';
import { User, Settings, Trash2, LogOut, ChevronDown, Bell } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useQuery } from 'react-query';
import { api } from '../lib/api';
import { handleLogout } from '../lib/logout';

interface CustomUserMenuProps {
  onOpenDeletedItems: () => void;
  onOpenNotifications: () => void;
  compact?: boolean;
}

export default function CustomUserMenu({ onOpenDeletedItems, onOpenNotifications }: CustomUserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch unread notifications count
  const { data: unreadCount } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get('/notifications/unread-count').then(res => res.data.data.count),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider stale after 10 seconds
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleManageAccount = () => {
    setIsOpen(false);
    openUserProfile();
  };

  const handleDeletedItems = () => {
    setIsOpen(false);
    onOpenDeletedItems();
  };

  const handleNotifications = () => {
    setIsOpen(false);
    onOpenNotifications();
  };

  const handleSignOut = () => {
    setIsOpen(false);
    handleLogout(signOut)();
  };

  if (!user) {
    return null;
  }

  const userInitials = user.firstName && user.lastName 
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user.emailAddresses[0]?.emailAddress?.[0]?.toUpperCase() || 'U';

  const userDisplayName = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : user.emailAddresses[0]?.emailAddress || 'User';

  return (
    <div className="relative" ref={menuRef}>
      {/* User Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-md hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Notification Badge */}
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center z-10">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
        {/* User Avatar */}
        <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
          {user.imageUrl ? (
            <img
              src={user.imageUrl}
              alt={userDisplayName}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <span className="text-primary-foreground font-medium text-sm">
              {userInitials}
            </span>
          )}
        </div>
        
        {/* Chevron */}
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                {user.imageUrl ? (
                  <img
                    src={user.imageUrl}
                    alt={userDisplayName}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-primary-foreground font-medium">
                    {userInitials}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-card-foreground truncate">
                  {userDisplayName}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.emailAddresses[0]?.emailAddress}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* My Tasks and Notifications */}
            <button
              onClick={handleNotifications}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-card-foreground hover:bg-muted transition-colors"
            >
              <div className="relative">
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center">
                    <span className="text-[8px] leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  </div>
                )}
              </div>
              <span>My Tasks and Notifications</span>
            </button>

            {/* Manage Account */}
            <button
              onClick={handleManageAccount}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-card-foreground hover:bg-muted transition-colors"
            >
              <User className="h-4 w-4" />
              <span>Manage Account</span>
            </button>

            {/* Deleted Items */}
            <button
              onClick={handleDeletedItems}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-card-foreground hover:bg-muted transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>Deleted Items</span>
            </button>

            {/* Divider */}
            <div className="border-t border-border my-2" />

            {/* Sign Out */}
            <button
              onClick={handleSignOut}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-card-foreground hover:bg-muted transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
